import { Component, OnDestroy, OnInit, ViewChild, HostListener, inject, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { FieldArrayType } from '@ngx-formly/core';
import { getFlattenedFieldsWithDetails } from '@xilo-mono/form-helper';
import { flattenDeep } from 'lodash';
import { ConfigService } from '@xilo-mono/common/config';
import { CsvImporterComponent } from '@xilo-mono/csv-importer';
import { FormViewService } from '@xilo-mono/form-contracts';
import { BehaviorSubject, ReplaySubject, merge, Subscription } from 'rxjs';
import { debounceTime, takeUntil, filter } from 'rxjs/operators';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { FormArray } from '@angular/forms';

@Component({
  selector: 'xilo-mono-form-viewer-intake-repeat',
  templateUrl: './intake-repeat.type.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class IntakeRepeatTypeComponent extends FieldArrayType implements OnInit, OnDestroy {
  @ViewChild('importer') importer: CsvImporterComponent;
  activeUser = {
    id: '1',
    name: 'Form Filler',
    email: 'customer-sucess@xilo.io',
    companyId: '1',
    companyName: 'Form Filler'
  };
  uploadClicked = false;
  openedAccordion = new Set<string>();
  brandColor?: string;
  private readonly destroyed$ = new ReplaySubject<boolean>(1);
  private readonly cdr = inject(ChangeDetectorRef);
  private readyToAddWatcherSub: Subscription;
  readonly readyToAdd$ = new BehaviorSubject<boolean>(false);
  readonly isReadyToAddRepeatItem$ = new BehaviorSubject<boolean>(false);
  private readonly formViewService = inject(FormViewService);
  private readonly configService = inject(ConfigService);

  constructor() {
    super();
  }

  @HostListener('document:keydown', ['$event'])
  handleHotkeyAddSection(e: KeyboardEvent): void {
    const isPlus = e.key === '+' || e.key === '=';
    const isMetaOrCtrl = this.isMac ? e.metaKey : e.ctrlKey;

    if (isPlus && isMetaOrCtrl && this.isReadyToAddRepeatItem$.value) {
      e.preventDefault();
      e.stopPropagation();
      this.addMore();
    }
  }

  get dromoLicenseKey(): string {
    return this.configService.dromoLicenseKey;
  }

  get isMac(): boolean {
    return this.formViewService.isMacUser;
  }

  get isIntakeForm(): boolean {
    return this.field.type.includes('intake');
  }

  ngOnInit(): void {
    if (this.field.fieldGroup && this.field.fieldGroup.length === 0) {
      this.add();
    }

    this.field.templateOptions = {
      ...this.field.templateOptions,
      onAdd: (i?, initialModel?, markAsDirty?) => {
        if (i || i === 0) {
          this.add(i, initialModel, { markAsDirty });
        } else {
          this.add();
          this.formViewService.updateForm.next(true);
        }
      },
      onRemove: (index: number) => {
        this.remove(index);
        this.computeIsReadyToAdd();
      }
    };

    this.formViewService.openedAccordions$.pipe(takeUntil(this.destroyed$)).subscribe(openSet => {
      this.openedAccordion = openSet;
      this.isReadyToAddRepeatItem$.next(this.computeIsReadyToAdd());
      this.cdr.markForCheck();
    });

    if (this.field.formControl) {
      this.field.formControl.statusChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => {
        this.computeIsReadyToAdd();
        this.cdr.markForCheck();
      });
    }

    this.formViewService.fieldIdsWithErrorInIntakeForm$.pipe(filter(Boolean), takeUntil(this.destroyed$)).subscribe(fieldsToOpen => {
      this.formViewService.mergeAccordionsToOpen(fieldsToOpen);
    });

    if (this.formViewService.formView?.metadata?.brandColor) {
      this.brandColor = this.formViewService.formView.metadata.brandColor;
      this.cdr.markForCheck();
    }
  }

  ngOnDestroy(): void {
    if (this.readyToAddWatcherSub) {
      this.readyToAddWatcherSub.unsubscribe();
    }
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  override add(index?: number, initialModel?: any, options: { markAsDirty: boolean } = { markAsDirty: false }) {
    super.add(index, initialModel, options);
    this.initReadyToAddWatcher();
  }

  private initReadyToAddWatcher(): void {
    if (this.readyToAddWatcherSub) {
      this.readyToAddWatcherSub.unsubscribe();
    }

    const fieldGroup = this.field.fieldGroup || [];
    if (fieldGroup.length === 0) return;

    const controlStatusChanges$ = fieldGroup
      .map((_, i) => {
        const control = this.field.formControl?.get(`${i}`);
        return control?.statusChanges || null;
      })
      .filter(Boolean);

    const formArrayStatus$ = this.field.formControl?.statusChanges;
    const formArrayValue$ = this.field.formControl?.valueChanges;

    this.readyToAddWatcherSub = merge(...controlStatusChanges$, formArrayStatus$, formArrayValue$)
      .pipe(debounceTime(100), takeUntil(this.destroyed$))
      .subscribe(() => {
        this.isReadyToAddRepeatItem$.next(this.computeIsReadyToAdd());
        this.cdr.markForCheck();
      });
  }

  private computeIsReadyToAdd(): boolean {
    const fieldGroup = this.field.fieldGroup ?? [];
    if (fieldGroup.length === 0) return false;

    const allSectionsValid = fieldGroup.every((_, i) => {
      const control = this.field.formControl?.get(`${i}`);
      return control?.valid;
    });

    const anySectionOpen = fieldGroup.some(fg => fg.id && this.openedAccordion?.has(fg.id));

    return allSectionsValid && anySectionOpen;
  }

  openDromo() {
    const type = 'intake-form';
    const fieldGroup = [this.field];

    const fieldGroups = getFlattenedFieldsWithDetails(type, fieldGroup, this.field.key as string);

    const flattenedFieldGroup = flattenDeep(fieldGroups);
    const dromoFields = [];
    const fieldsCopy = [];
    for (const question of flattenedFieldGroup) {
      dromoFields.push({
        label: question.fieldLabel,
        key: question.fieldKey
      });
      fieldsCopy.push(question);
    }
    if (fieldsCopy.length > 0 && dromoFields.length > 0) {
      this.importer.openDromo(dromoFields, fieldsCopy);
    } else {
      // this.toastService.showMessage('Could not load form');
    }
  }

  onCompletedImport(params: any) {
    try {
      const { newFields } = params;
      for (let i = 0; i < newFields.length; i++) {
        const section = newFields[i];
        const fieldToImport = {};
        if (section.some(f => f.value)) {
          for (const f of section) {
            if (f.value) {
              fieldToImport[f.fieldKey] = f.value;
            } else {
              fieldToImport[f.fieldKey] = null;
            }
          }
        }
        if (Object.keys(fieldToImport).length > 0) {
          this.field.templateOptions.onAdd(i, fieldToImport, true);
        }
      }
      this.cdr.markForCheck();
    } catch (error) {
      console.error(error);
      this.cdr.markForCheck();
    }
  }

  addMore() {
    this.add();
    const newId = this.field?.fieldGroup[this.field.fieldGroup.length - 1]?.id;
    const prevId = this.field?.fieldGroup[this.field.fieldGroup.length - 2]?.id;

    if (prevId) this.formViewService.updateOpenedAccordions(prevId, false);
    if (newId) this.formViewService.updateOpenedAccordions(newId, true);
    this.isReadyToAddRepeatItem$.next(this.computeIsReadyToAdd());
  }

  openChanged(id: string, open: boolean) {
    if (!id) return;
    this.formViewService.updateOpenedAccordions(id, open);
    this.isReadyToAddRepeatItem$.next(this.computeIsReadyToAdd());
  }

  drop(event: CdkDragDrop<any[]>) {
    const formControl = this.field.fieldGroup[event.previousIndex]?.parent?.formControl;
    if (formControl && formControl instanceof FormArray) {
      moveItemInArray(formControl.controls, event.previousIndex, event.currentIndex);
    }
    moveItemInArray(this.field.fieldGroup, event.previousIndex, event.currentIndex);
    moveItemInArray(this.model, event.previousIndex, event.currentIndex);
    this.field.fieldGroup.forEach((fg, index) => {
      fg.key = `${index}`;
    });
    this.formViewService.updateForm.next(true);
    this.cdr.detectChanges();
  }
}
